import { flare } from '@flareapp/js';

const publicFlareKey = '2HA6dELpRTdeLU6GopvBVxq2klCvFXNt';
const authenticatedAppFlareKey = 'O10Gs5QMedKVGUT5te5erSdK20bmxGAm';

const thirdPartyScriptPatterns = [
    '152media.info',
    '33across.com',
    'adform.com',
    'adnami.io',
    'adnxs.com',
    'adyoulike.com',
    'amazon-adsystem.com',
    'amxrtb.com',
    'appnexus.com',
    'connectad.io',
    'btloader.com',
    'consumable.com',
    'contextweb.com',
    'criteo.com',
    'doubleclick.net',
    'flashb.id',
    'freewheel.tv',
    'google.com',
    'googlesyndication.com',
    'googletagmanager.com',
    'google-analytics.com',
    'improvedigital.com',
    'cmp.inmobi.com',
    'indexexchange.com',
    'keymobile.se',
    'leeads.com',
    'leeadsadprofit.com',
    'lijit.com',
    'livewrapped.com',
    'lwadm.com',
    'media.net',
    'netricsales.com',
    'onetag.com',
    'openx.com',
    'pubmatic.com',
    'rhebus.works',
    'richaudience.com',
    'risecodes.com',
    'rubiconproject.com',
    'sharethrough.com',
    'smaato.com',
    'smartadserver.com',
    'smilewanted.com',
    'sonobi.com',
    'teads.tv',
    'themediagrid.com',
    'themobileagency.se',
    'triplelift.com',
    'video.unrulymedia.com',
    'xandr.com',
    'yahoo.com',
    'yieldmo.com',
    'yoc.com',
    'zetaglobal.net',
];

const applicationScriptPatterns = [
    '/build/',
    '/build-backoffice/',
    '/build-dashboard/',
    '/resources/js/',
];

type InitializeFlareOptions = {
    filterThirdPartyScripts?: boolean;
};

function containsAnyPattern(value: string, patterns: string[]): boolean {
    return patterns.some((pattern) => value.includes(pattern));
}

function applicationScriptPatternsWithOrigin(): string[] {
    if (typeof window === 'undefined') {
        return applicationScriptPatterns;
    }

    return [...applicationScriptPatterns, window.location.origin];
}

function isWebKitNetworkLoadFailure(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }

    return error.name === 'TypeError' && error.message === 'Load failed' && !error.stack;
}

/**
 * Safari / WebKit invokes autofill callbacks that reference this global; when it is missing,
 * the browser throws before application code runs. Not actionable for the app.
 *
 * @see https://stackoverflow.com/questions/72720387/cant-find-variable-autofillcallbackhandler
 */
function isWebKitAutofillCallbackHandlerError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }

    return (
        error.name === 'ReferenceError' && error.message.includes('_AutofillCallbackHandler')
    );
}

function isStacklessWebKitNetworkLoadFailureReport(report: {
    message?: string;
    exception_class?: string;
    stacktrace?: { file?: string | null }[];
}): boolean {
    return (
        report.exception_class === 'TypeError' &&
        report.message === 'Load failed' &&
        (report.stacktrace ?? []).length === 0
    );
}

function isWebKitAutofillCallbackHandlerReport(report: {
    message?: string;
    exception_class?: string;
}): boolean {
    return (
        report.exception_class === 'ReferenceError' &&
        (report.message?.includes('_AutofillCallbackHandler') ?? false)
    );
}

function isThirdPartyOnlyReport(report: {
    stacktrace?: { file?: string | null }[];
}): boolean {
    const stackFiles = (report.stacktrace ?? []).map((frame) => frame.file ?? '').join('\n');

    return (
        containsAnyPattern(stackFiles, thirdPartyScriptPatterns) &&
        !containsAnyPattern(stackFiles, applicationScriptPatternsWithOrigin())
    );
}

export function shouldIgnorePublicBrowserError(error: unknown): boolean {
    return isWebKitNetworkLoadFailure(error) || isWebKitAutofillCallbackHandlerError(error);
}

export function shouldIgnorePublicBrowserReport(report: {
    message?: string;
    exception_class?: string;
    stacktrace?: { file?: string | null }[];
}): boolean {
    return (
        isStacklessWebKitNetworkLoadFailureReport(report) ||
        isWebKitAutofillCallbackHandlerReport(report) ||
        isThirdPartyOnlyReport(report)
    );
}

export function initializeFlare(
    key: string | undefined,
    { filterThirdPartyScripts = false }: InitializeFlareOptions = {},
): boolean {
    if (!import.meta.env.PROD || !key) {
        return false;
    }

    if (filterThirdPartyScripts) {
        configureThirdPartyScriptFiltering();
    }

    flare.light(key);

    return true;
}

export function initializePublicFlare(): boolean {
    return initializeFlare(publicFlareKey, {
        filterThirdPartyScripts: true,
    });
}

export function initializeAuthenticatedAppFlare(): boolean {
    return initializeFlare(authenticatedAppFlareKey);
}

function configureThirdPartyScriptFiltering(): void {
    flare.beforeEvaluate = (error) => {
        if (shouldIgnorePublicBrowserError(error)) {
            return false;
        }

        const stack = error.stack ?? '';

        if (
            containsAnyPattern(stack, thirdPartyScriptPatterns) &&
            !containsAnyPattern(stack, applicationScriptPatternsWithOrigin())
        ) {
            return false;
        }

        return error;
    };

    flare.beforeSubmit = (report) => {
        if (shouldIgnorePublicBrowserReport(report)) {
            return false;
        }

        return report;
    };
}
